<script lang="ts">
	import {
		remoteUsers,
		spaceMembers,
		colorForUser,
	} from '../../lib/presence';
	import { currentUser } from '../../stores';

	type AvatarStatus = 'active' | 'idle' | 'offline' | 'local';

	interface AvatarUser {
		id: string;
		name: string;
		email: string;
		color: string;
		status: AvatarStatus;
	}

	const MAX_VISIBLE = 4;

	function getInitials(user: AvatarUser): string {
		const name = user.name || user.email;
		const parts = name.split(/[\s@]+/);
		if (parts.length >= 2 && parts[0] && parts[1]) {
			return (parts[0][0] + parts[1][0]).toUpperCase();
		}
		return (name[0] || '?').toUpperCase();
	}

	function statusLabel(status: AvatarStatus): string {
		if (status === 'local') return ' (you)';
		if (status === 'idle') return ' (idle)';
		if (status === 'offline') return ' (offline)';
		return '';
	}

	/**
	 * Build the combined avatar list, ordered left-to-right:
	 *  Left (bottom z):  overflow badge, offline members
	 *  Right (top z):    idle users, active users, local user (rightmost)
	 *
	 * Stacking: rightmost = highest z-index (via ascending z-index in CSS).
	 */
	let allUsers: AvatarUser[] = $derived.by(() => {
		const offline: AvatarUser[] = [];
		const idle: AvatarUser[] = [];
		const active: AvatarUser[] = [];
		const seenIds = new Set<string>();

		// Collect local user id so we can skip it from remote/members
		const local = $currentUser;
		if (local) seenIds.add(local.id);

		// Online remote users — partition into active/idle
		for (const ru of $remoteUsers) {
			seenIds.add(ru.id);
			const entry: AvatarUser = {
				id: ru.id,
				name: ru.name,
				email: ru.email,
				color: ru.color,
				status: ru.status,
			};
			if (ru.status === 'active') {
				active.push(entry);
			} else {
				idle.push(entry);
			}
		}

		// Offline members — people with access who aren't online
		for (const member of $spaceMembers) {
			if (seenIds.has(member.id)) continue;
			seenIds.add(member.id);
			offline.push({
				id: member.id,
				name: member.email,
				email: member.email,
				color: colorForUser(member.id),
				status: 'offline',
			});
		}

		// Order: offline (leftmost) → idle → active → local (rightmost)
		const list: AvatarUser[] = [...offline, ...idle, ...active];

		if (local) {
			list.push({
				id: local.id,
				name: local.name || local.email,
				email: local.email,
				color: colorForUser(local.id),
				status: 'local',
			});
		}

		return list;
	});

	// Trim from the left (offline end) to keep the rightmost (most active) visible
	let overflowCount = $derived(Math.max(0, allUsers.length - MAX_VISIBLE));
	let visibleUsers = $derived(allUsers.slice(overflowCount));
</script>

{#if visibleUsers.length > 0}
	<div class="presence-avatars">
		{#if overflowCount > 0}
			<div class="avatar overflow" title="{overflowCount} more">
				<span class="avatar-initials">+{overflowCount}</span>
			</div>
		{/if}
		{#each visibleUsers as user, i (user.id)}
			<div
				class="avatar"
				class:idle={user.status === 'idle'}
				class:offline={user.status === 'offline'}
				style="--user-color: {user.color}; z-index: {i + 1};"
				title="{user.name}{statusLabel(user.status)}"
			>
				<span class="avatar-initials">{getInitials(user)}</span>
			</div>
		{/each}
	</div>
{/if}

<style>
	.presence-avatars {
		display: flex;
		align-items: center;
	}

	.avatar {
		width: 34px;
		height: 34px;
		display: flex;
		align-items: center;
		justify-content: center;
		flex-shrink: 0;

		background: var(--user-color);
		border: 2.5px solid var(--panelSolid);
		border-radius: 50%;

		margin-left: -6px;

		transition: opacity 0.4s ease, filter 0.4s ease, transform 0.15s ease;
		cursor: default;
	}

	.avatar:first-child {
		margin-left: 0;
	}

	.avatar:hover {
		transform: scale(1.1);
		z-index: 100 !important;
	}

	.avatar.idle {
		opacity: 0.45;
		filter: grayscale(0.6);
	}

	.avatar.offline {
		opacity: 0.3;
		filter: grayscale(0.8);
	}

	.avatar.overflow {
		background: var(--muted);
		opacity: 0.7;
		z-index: 0;
	}

	.avatar-initials {
		font-size: 11px;
		font-weight: 700;
		color: white;
		user-select: none;
		letter-spacing: 0.02em;
		line-height: 1;
	}
</style>
